// Library (/pengetahuan, feature "library"): the user's own documents, newest
// first, and their projects. The Basis Pengetahuan moved to Regulasi (tab
// Basis Pengetahuan).
//   lib_header   plain spacing    h1 and how many documents and projects
//   lib_tabs     tabs             Dokumen | Project (?tab=project; Project needs the projects feature)
//   lib_toolbar  divided section  Dokumen: search, Jenis (Semua, Diunggah, Dibuat Pluto,
//                                 Dokumen aktivitas) and Project; Project: search
//   lib_list     rows             Dokumen: name, kind badge, project, date and size, Unduh
//                                 Project: name, kind, how many documents, last change,
//                                 Lihat dokumen (Dokumen filtered to it) and Buka project
// Uploads and Pluto's chat files download from Drive Pluto (or the user's own
// Drive for older files) through the attachment route; an activity document
// downloads its uploaded original or a DOCX of the saved version.
import { useState } from "react";
import { Link, navigate, useLocation } from "../router";
import { useFeatures } from "../shell/features";
import * as API from "../api";
import type { LibraryItem, Project } from "../api";
import { formatCount, formatDate } from "../lib/format";
import { formatFileSize } from "../project/files";
import { saveFile } from "../pbj/docx";
import { Badge, EmptyState, Notice, PageHeader, SearchInput, SkeletonRows, Tabs, useAsync, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";

export type LibraryFilter = "" | "upload" | "generated" | "activity";

export const LIBRARY_KIND_LABEL: Record<LibraryItem["kind"], string> = {
  upload: "Diunggah",
  generated: "Dibuat Pluto",
  activity: "Dokumen aktivitas",
};

/** Lowercase text with accents folded, for a forgiving search. */
function fold(text: string): string {
  return text.toLowerCase().normalize("NFKD").replace(/\p{M}/gu, "");
}

/** "" every project, "none" outside any project, else one project id. */
export type ProjectFilter = string;

export function filterLibrary(items: LibraryItem[], q: string, kind: LibraryFilter, project: ProjectFilter = ""): LibraryItem[] {
  const terms = fold(q).split(/\s+/).filter(Boolean);
  return items.filter((i) => {
    if (kind && i.kind !== kind) return false;
    if (project === "none" && i.projectId !== null) return false;
    if (project && project !== "none" && i.projectId !== project) return false;
    const hay = fold([i.name, i.projectName ?? ""].join(" "));
    return terms.every((t) => hay.includes(t));
  });
}

function metaOf(i: LibraryItem): string {
  const parts = [
    i.projectName ? `Project ${i.projectName}` : "Di luar project",
    i.kind === "activity" && i.version !== null ? `versi ${i.version}${i.edited ? ", hasil edit" : ""}` : "",
    formatDate(i.createdAt),
    i.size !== null ? formatFileSize(i.size) : "",
  ];
  return parts.filter(Boolean).join(" · ");
}

export const PROJECT_KIND_LABEL: Record<string, string> = { pbj: "Paket PBJ", paket: "Paket Penyedia", folder: "Folder" };

/** How many of the user's documents belong to each project. */
export function documentCounts(items: LibraryItem[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const i of items) if (i.projectId) counts.set(i.projectId, (counts.get(i.projectId) ?? 0) + 1);
  return counts;
}

export function LibraryScreen() {
  useTitle("Dokumen");
  const features = useFeatures();
  const projectsOn = features.list !== null && features.has("projects");
  const list = useAsync(() => API.getLibrary(), []);
  const projects = useAsync<Project[]>(() => (projectsOn ? API.listProjects() : Promise.resolve([])), [projectsOn]);
  const location = useLocation();
  const tab = projectsOn && location.query.get("tab") === "project" ? "project" : "dokumen";
  const setTab = (next: string) => navigate(next === "project" ? `${location.path}?tab=project` : location.path, { replace: true });
  const [q, setQ] = useState("");
  const [kind, setKind] = useState<LibraryFilter>("");
  const [project, setProject] = useState<ProjectFilter>("");
  const [projectQ, setProjectQ] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<{ key: string; message: string } | null>(null);

  const all = list.data ?? [];
  const shown = filterLibrary(all, q, kind, project);
  const projectList = projects.data ?? [];
  const counts = documentCounts(all);
  const projectShown = projectList.filter((p) => fold(p.name).includes(fold(projectQ.trim())));
  const showProjectDocs = (id: string) => {
    setProject(id);
    setKind("");
    setQ("");
    setTab("dokumen");
  };

  async function downloadActivity(item: LibraryItem) {
    if (item.download.type !== "activity" || busy) return;
    setBusy(item.key);
    setError(null);
    try {
      saveFile(await API.downloadActivityDoc(item.download.projectId, item.download.docId));
    } catch (err) {
      setError({ key: item.key, message: API.errorMessage(err) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <PageHeader
        divided
        title="Dokumen"
        lead={
          list.data
            ? `${formatCount(all.length)} dokumen${projectsOn && projects.data ? ` dan ${formatCount(projectList.length)} project` : ""}: yang Anda unggah, yang dibuat Pluto, dan dokumen aktivitas yang Anda simpan.`
            : "Memuat dokumen Anda"
        }
      />

      {projectsOn ? (
        <Tabs
          label="Isi Dokumen"
          idBase="lib"
          items={[
            { id: "dokumen", label: "Dokumen" },
            { id: "project", label: "Project" },
          ]}
          value={tab}
          onChange={setTab}
        />
      ) : null}

      {tab === "project" ? (
        <div id="lib-panel" role="tabpanel" aria-labelledby="lib-tab-project">
          <section className="toolbar" aria-label="Cari project">
            <div className="field-row" data-cols="3">
              <div className="field">
                <label htmlFor="lib-pq">Cari project</label>
                <SearchInput id="lib-pq" value={projectQ} onChange={setProjectQ} placeholder="Nama project" />
              </div>
            </div>
          </section>
          {projects.loading && !projects.data ? (
            <SkeletonRows count={4} label="Memuat project" />
          ) : projects.error ? (
            <Notice tone="danger" title="Project belum dapat dimuat" action={<button type="button" className="btn btn-secondary" onClick={projects.reload}>Muat ulang</button>}>
              <p>{API.errorMessage(projects.error)}</p>
            </Notice>
          ) : projectList.length === 0 ? (
            <EmptyState icon="folder" title="Belum ada project" body="Project yang Anda buat akan muncul di sini beserta dokumennya." />
          ) : projectShown.length === 0 ? (
            <EmptyState icon="filter" title="Tidak ada project yang cocok" body="Ubah kata kunci pencarian." />
          ) : (
            <ul className="rows lib-projects" aria-label="Project Anda">
              {projectShown.map((p) => {
                const n = counts.get(p.id) ?? 0;
                return (
                  <li key={p.id} className="row" data-kind={p.kind}>
                    <span className="row-main">
                      <span className="row-title">{p.name}</span>
                      <span className="row-meta">
                        <Badge>{PROJECT_KIND_LABEL[p.kind] ?? "Project"}</Badge> {formatCount(n)} dokumen · diperbarui {formatDate(p.updatedAt)}
                      </span>
                    </span>
                    <span className="row-side">
                      <button type="button" className="btn btn-secondary" disabled={n === 0} onClick={() => showProjectDocs(p.id)} aria-label={`Lihat dokumen ${p.name}`}>
                        <Glyph name="documentText" />
                        <span>Lihat dokumen</span>
                      </button>
                      <Link className="btn btn-secondary" href={`/project/${p.id}`} aria-label={`Buka project ${p.name}`}>
                        <Glyph name="folder" />
                        <span>Buka project</span>
                      </Link>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : (
        <div id="lib-panel" role={projectsOn ? "tabpanel" : undefined} aria-labelledby={projectsOn ? "lib-tab-dokumen" : undefined}>
          <section className="toolbar" aria-label="Cari dokumen">
            <div className="field-row" data-cols="3">
              <div className="field">
                <label htmlFor="lib-q">Cari dokumen</label>
                <SearchInput id="lib-q" value={q} onChange={setQ} placeholder="Nama dokumen atau project" />
              </div>
              {projectsOn ? (
                <div className="field">
                  <label htmlFor="lib-project">Project</label>
                  <select id="lib-project" value={project} onChange={(e) => setProject(e.currentTarget.value)}>
                    <option value="">Semua project</option>
                    <option value="none">Di luar project</option>
                    {projectList.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div className="field">
                <label htmlFor="lib-kind">Jenis</label>
                <select id="lib-kind" value={kind} onChange={(e) => setKind(e.currentTarget.value as LibraryFilter)}>
                  <option value="">Semua dokumen</option>
                  <option value="upload">Diunggah</option>
                  <option value="generated">Dibuat Pluto</option>
                  <option value="activity">Dokumen aktivitas</option>
                </select>
              </div>
            </div>
          </section>

          {list.loading && !list.data ? (
            <SkeletonRows count={6} label="Memuat dokumen" />
          ) : list.error ? (
            <Notice tone="danger" title="Dokumen belum dapat dimuat" action={<button type="button" className="btn btn-secondary" onClick={list.reload}>Muat ulang</button>}>
              <p>{API.errorMessage(list.error)}</p>
            </Notice>
          ) : all.length === 0 ? (
            <EmptyState icon="folder" title="Belum ada dokumen" body="File yang Anda unggah di chat atau project, dokumen yang dibuat Pluto, dan dokumen aktivitas yang Anda simpan akan muncul di sini." />
          ) : shown.length === 0 ? (
            <EmptyState icon="filter" title="Tidak ada dokumen yang cocok" body="Ubah kata kunci, atau pilih semua jenis dan semua project." />
          ) : (
            <ul className="rows" aria-label="Dokumen Anda">
              {shown.map((i) => (
                <li key={i.key} className="row" data-kind={i.kind}>
                  <span className="row-main">
                    <span className="row-title">{i.name}</span>
                    <span className="row-meta">
                      <Badge tone={i.kind === "upload" ? "neutral" : "info"}>{LIBRARY_KIND_LABEL[i.kind]}</Badge> {metaOf(i)}
                    </span>
                    {error?.key === i.key ? (
                      <span className="pk-item-error" role="alert">
                        <Glyph name="danger" size={16} />
                        <span>{error.message}</span>
                      </span>
                    ) : null}
                  </span>
                  <span className="row-side">
                    {i.download.type === "attachment" ? (
                      <a className="btn btn-secondary" href={API.attachmentDownloadUrl(i.download.id)} download={i.name} aria-label={`Unduh ${i.name}`}>
                        <Glyph name="documentDownload" />
                        <span>Unduh</span>
                      </a>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={busy !== null}
                        aria-busy={busy === i.key || undefined}
                        aria-label={`Unduh ${i.name}`}
                        onClick={() => void downloadActivity(i)}
                      >
                        <Glyph name="documentDownload" />
                        <span>{busy === i.key ? "Menyiapkan" : "Unduh"}</span>
                      </button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}
